import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import {
  creatorsListQuery,
  aggregatesQuery,
  creatorStatsFrom,
} from "@/lib/queries";
import { formatCompact } from "@/lib/format";
import { CountryLabel } from "@/components/country-label";
import { LayoutGrid, List as ListIcon, ArrowUp, ArrowDown } from "lucide-react";
import { Suspense } from "react";
import { SuggestYoutuberDialog } from "@/components/suggest-youtuber-dialog";
import { safeLoad } from "@/lib/ssr-safe";
import "@/i18n";

export const Route = createFileRoute("/youtubers")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Youtubers · Youtubers Map" },
      { property: "og:title", content: "Youtubers · Youtubers Map" },
      { property: "og:description", content: "Tots els youtubers viatgers agregats a Youtubers Map." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "description", content: "Tots els youtubers viatgers agregats a Youtubers Map." },
    ],
  }),
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(aggregatesQuery);
    await safeLoad("youtubers:creators", context.queryClient.ensureQueryData(creatorsListQuery));
  },
  component: ExplorersPage,
});

type SortKey = "name" | "country" | "subscriber_count" | "videos" | "countries";
type SortDir = "asc" | "desc";
type ViewMode = "grid" | "list";

function ExplorersPage() {
  const { t } = useTranslation();
  usePageMeta(t("meta.youtubersTitle"), t("meta.youtubersDesc"));
  return (
    <div className="min-h-screen">
      <SiteNav />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <p className="text-mono-caps text-amber mb-2">{t("explorers.kicker")}</p>
            <h1 className="text-3xl md:text-4xl mb-2">{t("explorers.title")}</h1>
          </div>
        </div>
        <Suspense fallback={<p className="text-ink/40">…</p>}>
          <List />
        </Suspense>
      </section>
      <SiteFooter />
    </div>
  );
}

function List() {
  const { t } = useTranslation();
  const { data: creators } = useSuspenseQuery(creatorsListQuery);
  const { data: agg } = useSuspenseQuery(aggregatesQuery);
  const stats = useMemo(() => creatorStatsFrom(agg, creators), [agg, creators]);

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortKey, setSortKey] = useState<SortKey>("subscriber_count");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const rows = useMemo(() => {
    const arr = creators.map((c) => {
      const primary = c.channels.find((ch) => ch.is_primary_channel) ?? c.channels[0];
      const maxSubs = c.channels.reduce((m, ch) => Math.max(m, ch.subscriber_count ?? 0), 0);
      return {
        id: c.id,
        slug: c.slug,
        name: c.name,
        avatar_url: c.avatar_url ?? primary?.avatar_url ?? null,
        country: c.country ?? primary?.country ?? null,
        subscriber_count: maxSubs,
        _videos: stats.get(c.id)?.videos ?? 0,
        _countries: stats.get(c.id)?.countries ?? 0,
      };
    });
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case "name":
          return dir * a.name.localeCompare(b.name);
        case "country":
          return dir * (a.country ?? "").localeCompare(b.country ?? "");
        case "subscriber_count":
          return dir * (a.subscriber_count - b.subscriber_count);
        case "videos":
          return dir * (a._videos - b._videos);
        case "countries":
          return dir * (a._countries - b._countries);
      }
    });
    return arr;
  }, [creators, stats, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "name" || k === "country" ? "asc" : "desc");
    }
  };

  if (rows.length === 0) {
    return (
      <p className="text-ink/50 py-12 text-center">
        {t("explorers.empty")}{" "}
        <Link to="/admin" className="text-amber underline">
          {t("explorers.backoffice")}
        </Link>.
      </p>
    );
  }

  return (
    <>
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center ring-hairline bg-paper">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 ${viewMode === "grid" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
            aria-label={t("explorers.grid")}
          >
            <LayoutGrid className="size-4" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 ${viewMode === "list" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
            aria-label={t("explorers.list")}
          >
            <ListIcon className="size-4" />
          </button>
        </div>
      </div>
      {viewMode === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
          {rows.map((y) => (
            <Link key={y.id} to="/y/$handle" params={{ handle: y.slug }} className="group">
              <div className="aspect-square bg-secondary ring-hairline overflow-hidden mb-2 group-hover:ring-amber/40 transition-all">
                {y.avatar_url ? (
                  <img src={y.avatar_url} alt={y.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-mono-caps text-ink/30">
                    {y.name.slice(0, 2)}
                  </div>
                )}
              </div>
              <h3 className="font-serif text-sm leading-tight truncate group-hover:text-amber transition-colors">
                {y.name}
              </h3>
              <div className="mt-1 font-mono text-[10px] text-ink/50 leading-tight">
                <div className="flex justify-between">
                  <span>{formatCompact(y.subscriber_count)}</span>
                  <CountryLabel code={y.country} name={y.country} showName={false} size={20} />
                </div>
                <div>{t("home.videoCount", { count: y._videos })} · {t("home.countriesCount", { count: y._countries })}</div>
              </div>
            </Link>
          ))}
          <SuggestYoutuberDialog
            trigger={
              <button
                type="button"
                className="text-left group flex flex-col"
              >
                <div className="aspect-square ring-hairline border border-dashed border-ink/25 mb-1.5 grid place-items-center bg-secondary/40 group-hover:border-amber transition-colors">
                  <span className="text-2xl text-ink/40 group-hover:text-amber transition-colors">+</span>
                </div>
                <span className="font-serif text-xs leading-tight group-hover:text-amber transition-colors">
                  {t("home.suggest.cta")}
                </span>
              </button>
            }
          />
        </div>
      ) : (
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10 bg-secondary/40">
              <tr>
                <th className="w-14 px-3 py-2" />
                <SortHead k="name" label={t("explorers.columns.name")} sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                <SortHead k="country" label={t("explorers.columns.country")} sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="w-40" />
                <SortHead k="subscriber_count" label={t("explorers.columns.subscribers")} sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="w-32" />
                <SortHead k="videos" label={t("explorers.columns.videos")} sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="w-24" />
                <SortHead k="countries" label={t("explorers.columns.countries")} sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8">
              {rows.map((y) => (
                <tr key={y.id} className="hover:bg-secondary/40 transition-colors">
                  <td className="px-3 py-1.5">
                    <Link to="/y/$handle" params={{ handle: y.slug }} className="block size-10 bg-secondary overflow-hidden rounded-full">
                      {y.avatar_url && <img src={y.avatar_url} alt="" className="w-full h-full object-cover" />}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5">
                    <Link to="/y/$handle" params={{ handle: y.slug }} className="font-serif hover:text-amber">
                      {y.name}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-ink/70 whitespace-nowrap">
                    <CountryLabel code={y.country ?? undefined} size={20} />
                  </td>
                  <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">{formatCompact(y.subscriber_count)}</td>
                  <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">{y._videos}</td>
                  <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">{y._countries}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} className="px-3 py-2">
                  <SuggestYoutuberDialog
                    trigger={
                      <button
                        type="button"
                        className="text-left group flex items-center gap-2"
                      >
                        <span className="size-10 ring-hairline border border-dashed border-ink/25 grid place-items-center bg-secondary/40 group-hover:border-amber transition-colors">
                          <span className="text-lg text-ink/40 group-hover:text-amber transition-colors">+</span>
                        </span>
                        <span className="font-serif text-xs group-hover:text-amber transition-colors">
                          {t("home.suggest.cta")}
                        </span>
                      </button>
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function SortHead({
  k,
  label,
  sortKey,
  sortDir,
  onToggle,
  className = "",
}: {
  k: SortKey;
  label: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onToggle: (k: SortKey) => void;
  className?: string;
}) {
  return (
    <th className={`text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 ${className}`}>
      <button onClick={() => onToggle(k)} className="inline-flex items-center gap-1 hover:text-ink">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );
}
